/**
 * @file CardLookFields.tsx
 * @description Вид карточек числами — общие поля «Оформление → Кнопки и
 * карточки → Карточки и фото» и «Каталог → Карточка товара → Вид карточки»
 * («Свои») (phase19.16, партия SB120, этап 2; SB11, SB31, SB120; проект
 * uiux-sb120-stage2 §2, §5; решение пользователя 2026-10-12 — поля одни,
 * разный охват):
 *
 * ```
 * Быстро: [Нет 0/0] [Мягкое 8/6] [Крупное 20/12]  ← ставит оба числа сразу
 * Карточки и фото  ├──●────┤ [  8 ] px
 * Поля и бейджи    ├─●─────┤ [  6 ] px            ← только у темы
 * Рамка: толщина / цвет · Тень                     ← CardBorderFields
 * ! Без рамки и тени карточка сливается с фоном…   ← предупреждение, не запрет
 * ┌ образец ┐
 * ```
 *
 * Контракт `value/onChange` — `CardLookValue` (card-shapes.ts): у карточки
 * товара `field` нет, схемы ставят только скругление карточки.
 */

import { SITE_THEME_LIMITS, type SiteColors } from "@chaiberi/shared";
import type { ReactNode } from "react";

import { NoticeStrip } from "../../ui/NoticeStrip";
import { NumberSliderField } from "../../site-fields/NumberSliderField";
import { CardBorderFields } from "./CardBorderFields";
import { CARD_LOOK_SCHEMES, cardLookWarnings, type CardLookValue } from "../../../lib/site-view/card-shapes";
import { cn } from "@/lib/utils";

const { radius: R } = SITE_THEME_LIMITS;

interface Props {
  value: CardLookValue;
  onChange: (value: CardLookValue) => void;
  /** Живой предпросмотр во время перетаскивания (не запись). */
  onPreview?: (value: CardLookValue) => void;
  colors: SiteColors;
  /** Чьи карточки — в подписях: «карточек», «карточки товара». */
  of: string;
  /** Подпись скругления карточки: «Карточки и фото», «Скругление карточки». */
  radiusLabel: string;
  /** Образец под полями. */
  sample?: ReactNode;
}

export function CardLookFields({ value, onChange, onPreview, colors, of, radiusLabel, sample }: Props) {
  const withField = value.field !== undefined;
  const warnings = cardLookWarnings(value.cards, colors);

  return (
    <div className="flex min-w-0 flex-col gap-3">
      <div role="group" aria-label={`Скругление ${of}: быстрые схемы`} className="flex flex-wrap items-center gap-1">
        <span className="type-caption mr-1 text-on-surface-variant">Быстро:</span>
        {CARD_LOOK_SCHEMES.map((scheme) => {
          const active = value.radius === scheme.card && (!withField || value.field === scheme.field);
          return (
            <button
              key={scheme.id}
              type="button"
              aria-pressed={active}
              onClick={() => onChange({ ...value, radius: scheme.card, ...(withField ? { field: scheme.field } : {}) })}
              className={cn(
                "inline-flex h-12 items-center rounded-lg px-3 text-sm font-medium outline-none focus-visible:ring-2 focus-visible:ring-ring can-hover:h-8",
                active ? "bg-surface-lowest text-on-surface [box-shadow:inset_0_0_0_2px_var(--primary)]" : "bg-surface-low text-on-surface-variant hover:text-on-surface",
              )}
            >
              {scheme.label} {withField ? `${scheme.card}/${scheme.field}` : scheme.card}
            </button>
          );
        })}
      </div>
      <NumberSliderField
        label={radiusLabel}
        value={value.radius}
        inherit={{ value: 0, label: "0 px" }}
        min={R.min}
        max={R.max}
        step={1}
        unit="px"
        display={{ factor: 1, decimals: 2 }}
        onChange={(v) => onChange({ ...value, radius: v ?? 0 })}
        onPreview={onPreview ? (v) => onPreview({ ...value, radius: v ?? 0 }) : undefined}
      />
      {withField ? (
        <NumberSliderField
          label="Поля и бейджи"
          value={value.field}
          inherit={{ value: 0, label: "0 px" }}
          min={R.min}
          max={R.max}
          step={1}
          unit="px"
          display={{ factor: 1, decimals: 2 }}
          hint="Поля ввода, бейджи, плашки подсказок."
          onChange={(v) => onChange({ ...value, field: v ?? 0 })}
          onPreview={onPreview ? (v) => onPreview({ ...value, field: v ?? 0 }) : undefined}
        />
      ) : null}
      <CardBorderFields
        cards={value.cards}
        colors={colors}
        of={of}
        onChange={(cards) => onChange({ ...value, cards })}
        onPreview={onPreview ? (cards) => onPreview({ ...value, cards }) : undefined}
      />
      {warnings.map((text) => (
        <NoticeStrip key={text} tone="warning">
          {text}
        </NoticeStrip>
      ))}
      {sample}
    </div>
  );
}
